/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, computed, Inject, OnDestroy, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {StateService, UIRouter} from '@uirouter/core';
import * as _ from 'lodash';

import template from './search-page.component.html';
import {ResourceHelper} from '../../components/util/resource-helper';
import {ObjectTypeFinder} from '../common/constants/object-type';
import {objectTypeStateParams} from '../common/filters/object-type-state-params';
import {SearchCoordinatorService} from './search-coordinator.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {BrowserLocationService} from '../angular-platform-providers';
import {AppEventBusService} from '../common/events/app-event-bus.service';
import {APP_SHARED} from '../common/app-shared';
import {ADMIN_LIST} from '../common/directives/admin-list';

const LABEL_CAPS = ['LABEL', 'RATE_CONTROL_LABELS', 'EXCLUSION_LABEL', 'MATCHING_LABEL'];

@Component({
  standalone: true,
  selector: 'adn-search-page',
  imports: [...APP_SHARED, FormsModule, TranslateModule, ...ADMIN_LIST],
  template
})
export class SearchPageComponent implements OnInit, OnDestroy {
  term: string;
  labels: string;
  rateControlLabels: string;
  aggregations: any[] = [];
  includeDeleted = false;
  isPureMarketplace = false;
  private readonly syncingState = signal(false);
  private readonly searchResultsState = signal<any>({items: []});
  searchTerm: string;
  searchMeta: any;

  private destroyLocationListener: () => void;

  get syncing(): boolean { return this.syncingState(); }
  set syncing(value: boolean) { this.syncingState.set(value); }
  get searchResults(): any { return this.searchResultsState(); }
  set searchResults(value: any) { this.searchResultsState.set(value || {items: []}); }

  readonly displaySyncing = computed(() => this.syncingState());
  readonly hasZeroHits = computed(() => this.searchResultsState()?.totalHits === 0);
  readonly hasHits = computed(() => !!this.searchResultsState()?.totalHits);

  constructor(
    private readonly stateService: StateService,
    @Inject(BrowserLocationService) private readonly location: any,
    private readonly translate: TranslateService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly searchCoordinator: SearchCoordinatorService,
    private readonly uiRouter: UIRouter,
    private readonly eventBus: AppEventBusService
  ) {
  }

  ngOnInit(): void {
    const routeParams = this.uiRouter.globals.params;
    this.term = ResourceHelper.uiSrefUpdater(routeParams.q);
    this.labels = ResourceHelper.uiSrefUpdater(routeParams.labels);
    this.rateControlLabels = routeParams.rateControlLabels;
    this.aggregations = this.searchCoordinator.aggregations;
    this.includeDeleted = _.get(routeParams, 'objectState', '').indexOf('HIDDEN') > -1;
    this.isPureMarketplace = this.LocalNetworkProfile.isPureMarketplacePlatform();
    this.searchResults = this.searchCoordinator.searchResults;

    this.searchMeta = {
      pageSize: 50,
      changePage: () => {
        this.toggleFilter('page', this.searchMeta.currentPage);
        this.syncSearch();
      },
      resolved: true
    };

    this.syncSearch();

    this.destroyLocationListener = this.eventBus.on('$locationChangeSuccess', this.syncSearch);
  }

  ngOnDestroy(): void {
    if (this.destroyLocationListener) {
      this.destroyLocationListener();
    }
  }

  submit(term: string): void {
    this.stateService.go(this.stateService.current, {q: term}, {inherit: false});
  }

  labelSearch(label: any): void {
    this.stateService.go(this.stateService.current, objectTypeStateParams(label), {inherit: false});
  }

  includeDeletedChange(): void {
    const currentSearch = this.location.queryParams;
    currentSearch.objectState = this.includeDeleted ? 'HIDDEN;ACTIVE;INACTIVE' : 'ACTIVE;INACTIVE';
    this.location.setQueryParams(currentSearch);
    this.syncSearch();
  }

  toggleFilter(param: string, value?: any): void {
    const currentSearch = this.location.queryParams;
    if (param !== 'page') {
      delete currentSearch.page;
    }
    if (currentSearch[param] && (param !== 'page' || value < 2)) {
      delete currentSearch[param];
      this.location.setQueryParams(currentSearch);
      this.syncSearch();
      return;
    }
    this.location.setQueryParam(param, value);
    this.syncSearch();
  }

  getFilterValue(aggregation: any): string {
    const filter = this.location.queryParams[aggregation.name];
    return _.get(_.find(aggregation.buckets, ['filter', filter]), 'name', filter);
  }

  appliedAggregations(): any[] {
    return this.filterAggregations(false);
  }

  availableAggregations(): any[] {
    return this.filterAggregations(true);
  }

  aggregationHeading(aggregationName: string): string {
    const key = 'search.aggregation.' + aggregationName;
    const translated = this.translate.instant(key);
    if (_.isString(translated) && translated !== key) {
      return translated;
    }

    const nestedKey = key + '.' + aggregationName;
    const nestedTranslation = this.translate.instant(nestedKey);
    return _.isString(nestedTranslation) && nestedTranslation !== nestedKey
      ? nestedTranslation
      : _.startCase(aggregationName);
  }

  aggregationLabel(aggregationName: string, value: string, isEarningsAccount = false): string {
    const marketplaceSuffix = isEarningsAccount && this.isPureMarketplace ? '.marketplace' : '';
    const key = 'search.aggregation' + marketplaceSuffix + '.' + aggregationName + '.' + value;
    const translated = this.translate.instant(key);
    if (_.isString(translated) && translated !== key) {
      return translated;
    }
    if (aggregationName === 'types') {
      const objectType = ObjectTypeFinder.findSearchObject(value);
      if (objectType?.name) {
        return objectType.name;
      }
    }
    return value;
  }

  isStandardResult(result: any): boolean {
    return LABEL_CAPS.indexOf(result.objectType.caps) === -1 && result.objectType.caps !== 'USER_SEGMENT';
  }

  isLabelResult(result: any): boolean {
    return LABEL_CAPS.indexOf(result.objectType.caps) > -1;
  }

  isUserSegmentResult(result: any): boolean {
    return result.objectType.caps === 'USER_SEGMENT';
  }

  getResultHref(result: any): string {
    if (!result?.objectType?.stateName) {
      return '#';
    }
    return this.stateService.href(result.objectType.stateName, objectTypeStateParams(result));
  }

  private filterAggregations(inverse: boolean): any[] {
    const filters = this.location.queryParams;
    return _.filter(this.aggregations, (aggregation) => {
      const isFiltered = filters[aggregation.name];
      return inverse ? !isFiltered : isFiltered;
    });
  }

  private syncSearch = (): void => {
    if (this.syncing) {
      return;
    }
    this.syncing = true;
    const params = this.location.queryParams;
    this.searchMeta.currentPage = params.page || 1;
    this.searchCoordinator.sync(params, this.searchMeta).then(() => {
      this.searchResults = {...this.searchCoordinator.searchResults};
      this.syncing = false;
      this.term = params.q;
      this.searchTerm = params.q;
      this.labels = params.labels;
    }).catch(() => {
      this.searchResults = {...this.searchCoordinator.searchResults};
      this.syncing = false;
    });
  };
}
